import { DollarSign, MapPin, Phone, Mail, Clock, Shield, Users, CheckCircle, Star, Award, CreditCard, Zap, Building, FileText, Calculator, TrendingUp, BookOpen, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const FAQ = () => {
  const faqCategories = [
    {
      title: "General Questions",
      faqs: [
        {
          question: "What is QuickLoanPro?",
          answer: "QuickLoanPro is a licensed financial service provider offering payday loans, personal loans, and cash advances across Louisiana and the United States. We specialize in quick, transparent lending with same-day approval and funding."
        },
        {
          question: "How quickly can I get my loan?",
          answer: "Most approved loans are funded the same day. For applications submitted before 2 PM CT on business days, funds are typically available within hours. Weekend applications are processed on the next business day."
        },
        {
          question: "What are your office hours?",
          answer: "Our office hours are Monday-Friday: 8:00 AM - 6:00 PM, Saturday: 9:00 AM - 6:00 PM, and Sunday: 9:00 AM - 6:00 PM. Our online application is available 24/7."
        },
        {
          question: "Is QuickLoanPro licensed and regulated?",
          answer: "Yes, QuickLoanPro is a fully licensed and regulated financial service provider. We comply with all state and federal lending regulations and are committed to responsible lending practices."
        }
      ]
    },
    {
      title: "Loan Application Process",
      faqs: [
        {
          question: "How do I apply for a loan?",
          answer: "Simply visit our website and complete our secure online application. The process takes just 3-5 minutes and requires basic personal, employment, and banking information."
        },
        {
          question: "What information do I need to apply?",
          answer: "You'll need your Social Security number, valid ID, proof of income, active checking account information, and contact details including phone number and email address."
        },
        {
          question: "Can I apply if I'm unemployed?",
          answer: "You need a steady source of income to qualify, but this doesn't have to be traditional employment. We accept income from benefits, pensions, disability, or other regular sources."
        },
        {
          question: "Do you verify my employment?",
          answer: "Yes, we may contact your employer to verify employment and income. This is a standard part of our approval process to ensure responsible lending."
        }
      ]
    },
    {
      title: "Credit and Approval",
      faqs: [
        {
          question: "Do you check my credit score?",
          answer: "We may perform a soft credit check that doesn't affect your credit score. Our approval is primarily based on your income and ability to repay rather than your credit history."
        },
        {
          question: "What if I have bad credit?",
          answer: "Bad credit is not automatically disqualifying. We focus on your current financial situation and ability to repay. Many customers with poor credit have been approved for loans."
        },
        {
          question: "Will getting a loan affect my credit score?",
          answer: "The initial application involves a soft credit check that doesn't impact your score. However, late payments or defaults may be reported to credit bureaus and could affect your credit."
        },
        {
          question: "What are the approval requirements?",
          answer: "You must be at least 18 years old, have a steady income, own an active checking account, provide valid contact information, and be a US citizen or permanent resident."
        }
      ]
    },
    {
      title: "Loan Terms and Fees",
      faqs: [
        {
          question: "How much can I borrow?",
          answer: "Payday loans range from $100 to $1,000, while personal loans can go up to $5,000. The exact amount depends on your income, state regulations, and our underwriting criteria."
        },
        {
          question: "What are your interest rates?",
          answer: "Interest rates vary based on loan type, amount, term, and your financial profile. All rates and fees are clearly disclosed before you accept your loan - no hidden costs."
        },
        {
          question: "Are there any hidden fees?",
          answer: "Absolutely not. We believe in complete transparency. All fees, including origination fees, interest rates, and any other charges, are clearly disclosed upfront before you accept your loan."
        },
        {
          question: "What are the repayment terms?",
          answer: "Payday loans are typically due on your next payday (2-4 weeks). Personal loans offer longer terms from 3-36 months with fixed monthly payments. Specific terms depend on your loan type and amount."
        }
      ]
    },
    {
      title: "Funding and Repayment",
      faqs: [
        {
          question: "How do I receive my loan funds?",
          answer: "Approved loan funds are deposited directly into your checking account via ACH transfer. This is the fastest and most secure method of funding."
        },
        {
          question: "When will I receive my money?",
          answer: "Same-day funding is available for most approved loans. Funds are typically available within hours for applications approved before 2 PM CT on business days."
        },
        {
          question: "How do I repay my loan?",
          answer: "Repayment is automatic via ACH debit from your checking account on the due date. You'll receive reminders before each payment is due."
        },
        {
          question: "What if I can't repay on time?",
          answer: "Contact us immediately if you're having trouble making a payment. We may be able to work out a payment plan or extension. Additional fees may apply for late payments or extensions."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-100">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img 
                src="https://quickloanpro.com/storage/2023/11/Payday-Loans-Louisiana-Logo.png" 
                alt="QuickLoanPro Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">QuickLoanPro</h1>
                <p className="text-gray-600">Your Trusted Financial Partner</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-gray-700">+1 (504) 438-1753</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-blue-600 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-blue-200 transition-colors">Home</Link>
            <Link to="/payday-loans" className="hover:text-blue-200 transition-colors">Payday Loans</Link>
            <Link to="/personal-loans" className="hover:text-blue-200 transition-colors">Personal Loans</Link>
            <Link to="/how-it-works" className="hover:text-blue-200 transition-colors">How It Works</Link>
            <Link to="/faq" className="font-semibold">FAQ</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
            Find answers to common questions about QuickLoanPro loans, our application process, and lending terms.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl px-8 py-4"
              onClick={() => window.open('https://quickloanpro.com/', '_blank')}
            >
              Apply Now
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-blue-600 font-bold text-xl px-8 py-4"
            >
              Call +1 (504) 438-1753
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <h2 className="text-3xl font-bold mb-8 text-gray-900 text-center">
                  {category.title}
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {category.faqs.map((faq, faqIndex) => (
                    <Card key={faqIndex} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-semibold mb-3 text-blue-800">
                          {faq.question}
                        </h3>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {faq.answer}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">
              Still Have Questions?
            </h2>
            <p className="text-gray-700 mb-8 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our friendly customer service team is here to help. Contact us during business hours or apply online 24/7.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <Phone className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Call Us</h3>
                <p className="text-gray-600 mb-4">Speak with our loan specialists</p>
                <p className="text-2xl font-bold text-green-600">+1 (504) 438-1753</p>
                <p className="text-sm text-gray-500 mt-2">
                  Mon-Fri: 8AM-6PM<br />
                  Sat-Sun: 9AM-6PM
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <Mail className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Email Us</h3>
                <p className="text-gray-600 mb-4">Send us your questions</p>
                <p className="text-lg font-semibold text-blue-600">info@quickloanpro.com</p>
                <p className="text-sm text-gray-500 mt-2">
                  We respond within 24 hours
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <Zap className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Apply Online</h3>
                <p className="text-gray-600 mb-4">Quick 3-minute application</p>
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-3"
                  onClick={() => window.open('https://quickloanpro.com/', '_blank')}
                >
                  Start Application
                </Button>
                <p className="text-sm text-gray-500 mt-2">
                  Available 24/7
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-green-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Explore Our Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Payday Loans</h3>
                <p className="text-sm text-gray-600 mb-4">Quick cash until payday</p>
                <Link to="/payday-loans">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <CreditCard className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Personal Loans</h3>
                <p className="text-sm text-gray-600 mb-4">Larger amounts, flexible terms</p>
                <Link to="/personal-loans">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <BookOpen className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">How It Works</h3>
                <p className="text-sm text-gray-600 mb-4">Learn our process</p>
                <Link to="/how-it-works">
                  <Button variant="outline" size="sm">Read Guide</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <Calculator className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Loan Calculator</h3>
                <p className="text-sm text-gray-600 mb-4">Calculate payments</p>
                <Link to="/loan-calculator">
                  <Button variant="outline" size="sm">Calculate</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Apply?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Got your questions answered? Apply now and get approved in minutes.
          </p>
          <Button 
            size="lg" 
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl px-8 py-4"
            onClick={() => window.open('https://quickloanpro.com/', '_blank')}
          >
            Apply for Loan Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">QuickLoanPro</h3>
              <p className="text-gray-400 mb-4">
                Your trusted partner for loans and financial assistance across Louisiana.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 (504) 438-1753</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@quickloanpro.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>4901 Bloomfield St, New Orleans, LA 70121</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Loan Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/payday-loans" className="hover:text-white">Payday Loans</Link></li>
                <li><Link to="/personal-loans" className="hover:text-white">Personal Loans</Link></li>
                <li><Link to="/installment-loans" className="hover:text-white">Installment Loans</Link></li>
                <li><Link to="/cash-advances" className="hover:text-white">Cash Advances</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/loan-calculator" className="hover:text-white">Loan Calculator</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                <li><Link to="/responsible-lending" className="hover:text-white">Responsible Lending</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 QuickLoanPro. All rights reserved.</p>
            <p className="mt-2">Licensed financial service provider committed to responsible lending practices.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;